import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Download } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n/i18n";

const LINKS = [
  { to: "/", key: "nav.home" },
  { to: "/learn", key: "nav.infos" },
  { to: "/about", key: "nav.about" },
];

function LangSwitch({ dark = true, suffix = "desktop" }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`inline-flex rounded-full border overflow-hidden text-[11px] font-bold tracking-widest ${
        dark ? "border-cream/20" : "border-ink/30"
      }`}
      data-testid={`lang-switch-${suffix}`}
    >
      {["fr", "en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}-btn-${suffix}`}
          className={`px-2.5 py-1 uppercase transition-colors ${
            lang === l ? "bg-gold text-ink" : dark ? "text-cream/60 hover:text-goldlight" : "text-ink/50 hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export { LangSwitch };

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 24);
    f();
    window.addEventListener("scroll", f, { passive: true });
    return () => window.removeEventListener("scroll", f);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const goDownload = () => {
    setOpen(false);
    navigate("/telecharger");
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-gold/10" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key.split(".")[1]}`}
              className={`text-[13px] tracking-[0.14em] uppercase transition-colors hover:text-goldlight ${
                pathname === l.to ? "text-goldlight" : "text-cream/70"
              }`}
            >
              {t(l.key)}
            </Link>
          ))}
          <LangSwitch />
          <button
            onClick={goDownload}
            data-testid="nav-download-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-semibold tracking-wide text-ink transition-all hover:bg-goldlight hover:shadow-[0_0_28px_rgba(212,175,55,0.35)]"
          >
            <Download size={15} className="transition-transform group-hover:translate-y-0.5" />
            {t("nav.download")}
          </button>
        </nav>

        <button
          className="md:hidden text-cream p-2.5 -mr-2.5"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-ink/95 backdrop-blur-xl border-t border-gold/10 px-6 pb-6 pt-2 flex flex-col gap-4">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.key.split(".")[1]}`}
              className="py-2 text-sm tracking-[0.14em] uppercase text-cream/80"
            >
              {t(l.key)}
            </Link>
          ))}
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.2em] text-mutedc">FR / EN / ES</span>
            <LangSwitch suffix="mobile" />
          </div>
          <button
            onClick={goDownload}
            data-testid="nav-mobile-download-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink"
          >
            <Download size={15} /> {t("nav.download")}
          </button>
        </div>
      )}
    </header>
  );
}
